import React, { useCallback, useContext, useMemo, useState } from 'react';
import classnames from 'classnames';
import './Expertise.scss';
import {
    ExpertiseWidgetType,
    getExpertiseWidgetStrategy,
} from './ExpertiseWidgetStrategy';
import ExpertiseToolbar from './toolbar/ExpertiseToolbar';
import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import AppContext from 'app/AppContext';
import { ExpertiseResponseDataItem } from 'communication/types/expertise/ExpertiseResponse';
import { useFilteredExpertiseData } from './hooks';
import {
    ExpertiseColumn,
    expertiseColumnDefinitions,
} from 'widgets/expertise/ExpertiseColumnDefinitions';
import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { useWidgetHasValidSelection } from 'widgets/hooks';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useExpertiseConfigurationSelector } from 'widgets/expertise/store/configuration/selectors';
import styleConstants from 'styles/variables.module.scss';
import { useWidgetConfigState } from 'widgets/store/configuration/hooks';
import { ExpertiseConfigurationState } from 'widgets/expertise/store/configuration/ExpertiseConfigurationState';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

export default function Expertise() {
    const { widgetKey, widgetType, height } = useWidgetContext();
    const expertiseWidgetType = widgetType as ExpertiseWidgetType;
    const config = useWidgetConfigState<ExpertiseConfigurationState>();

    const { invokeRpcRequest } = useWidgetStream();

    const context = useContext(AppContext);
    const useWidgetEditedColumnNamesProps = useWidgetEditedColumnNames();

    const hasValidSelection = useWidgetHasValidSelection();

    const { hideToolbar } = useExpertiseConfigurationSelector();

    const { buildColumnDefs, title } =
        getExpertiseWidgetStrategy(expertiseWidgetType);

    const allColumnDefs = useMemo(
        () => buildColumnDefs(context),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config, context]
    );

    const [gridApi, setApi] = useState<GridApi | undefined>();
    const onGridReady = (api: GridApi) => {
        setApi(api);
    };

    const { gridPropsFromState, columnDefs, visibleColumns, filters } =
        useWidgetGridState<ExpertiseResponseDataItem, ExpertiseColumn>(
            expertiseColumnDefinitions,
            allColumnDefs
        );

    const data = useFilteredExpertiseData(visibleColumns, filters);

    const rowCount = (data ?? []).length;

    const onRefresh = useCallback(invokeRpcRequest, [invokeRpcRequest]);

    const toolbarConsumedHeight = hideToolbar ? 0 : toolbarHeight;
    const contentHeight =
        height - padding - borderWidth - toolbarConsumedHeight;
    const contentStyle = { height: `${contentHeight}px` };

    return (
        <div
            className={classnames(
                'Expertise',
                expertiseWidgetType,
                hasValidSelection ? '' : 'hide-grid'
            )}
            data-widget-id={widgetKey}
        >
            <ExpertiseToolbar
                rowCount={rowCount}
                title={title}
                onRefresh={onRefresh}
                visibleColumns={visibleColumns ?? []}
                filters={filters ?? []}
                gridApi={gridApi}
            />

            <div className="ExpertiseContent" style={contentStyle}>
                <Grid
                    className="ExpertiseGrid"
                    columnDefs={columnDefs}
                    rowData={data || []}
                    getRowDataId={(r) => r.seq.toString()}
                    suppressColumnVirtualisation={false}
                    getRowClass={getRowClass}
                    onGridReady={onGridReady}
                    {...gridPropsFromState}
                    {...useWidgetEditedColumnNamesProps}
                />
            </div>
        </div>
    );
}

function getRowClass({
    data: rowData,
}: {
    data: ExpertiseResponseDataItem;
}): string {
    switch (rowData.EventType) {
        case 'Cancel':
        case 'Prevent':
            return 'row-cancel-prevent';
        case 'Mod':
            return 'row-mod';
        case 'New':
            return 'row-new';
        case 'Exec':
            return 'row-exec';
        default:
            return 'row-default';
    }
}
